import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChatCircle, MagnifyingGlass } from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MessagesPage() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/conversations`, { withCredentials: true });
      setConversations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.participant_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    try {
      return formatDistanceToNow(new Date(isoString), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <div className="app-container" data-testid="messages-page">
      {/* Header */}
      <header className="p-6 pb-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            <ChatCircle size={24} weight="duotone" className="inline mr-2 text-[#9D4CDD]" />
            Messages
          </h1>
          <p className="text-[#A1A1AA] text-sm">Connect with fellow gamers</p>
        </motion.div>
      </header>

      {/* Search */}
      <div className="px-6 mb-4">
        <div className="relative">
          <MagnifyingGlass 
            size={20} 
            weight="duotone" 
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717A]" 
          />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-12"
            data-testid="messages-search"
          />
        </div>
      </div>

      {/* Conversations List */}
      <div className="px-6 pb-32">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 rounded-2xl bg-[#0F0F16] animate-pulse"></div>
            ))}
          </div>
        ) : filteredConversations.length > 0 ? (
          <div className="space-y-2" data-testid="conversations-list">
            {filteredConversations.map((conv, index) => (
              <motion.div
                key={conv.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(`/chat/${conv.participant_id}`)}
                className="p-4 rounded-2xl bg-[#0F0F16] border border-white/5 cursor-pointer hover:border-[#9D4CDD]/30 transition-all"
                data-testid={`conversation-${conv.participant_id}`}
              >
                <div className="flex items-center gap-3">
                  {conv.participant_avatar ? (
                    <img 
                      src={conv.participant_avatar} 
                      alt={conv.participant_name} 
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="avatar">
                      {getInitials(conv.participant_name)}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-white truncate">{conv.participant_name}</h3>
                      {conv.last_message_time && (
                        <span className="text-[#71717A] text-xs">{formatTime(conv.last_message_time)}</span>
                      )}
                    </div>
                    <p className="text-[#A1A1AA] text-sm truncate">{conv.last_message || 'No messages yet'}</p>
                  </div>

                  {conv.unread_count > 0 && (
                    <div className="unread-badge">
                      {conv.unread_count > 9 ? '9+' : conv.unread_count}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <ChatCircle size={48} weight="duotone" className="text-[#71717A] mx-auto mb-4" />
            <p className="text-[#71717A]">No conversations yet</p>
            <p className="text-[#A1A1AA] text-sm mt-1">Start chatting with players!</p>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
